import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useState } from "react";
import { DilemmasHttpRepository } from "../repository";
import { DILEMMAS_STATUS } from "../constants";
import { useSubmissionsFilterStore } from "../stores";
import { useDebounce } from "@/hooks/useDebounce";

const PAGE_SIZE = 10;
const dilemmasRepository = new DilemmasHttpRepository();

export function useSubmissionsList() {
  const [page, setPage] = useState(1);
  const searchValue = useSubmissionsFilterStore((s) => s.searchValue);
  const ordering = useSubmissionsFilterStore((s) => s.ordering);
  const languageFilter = useSubmissionsFilterStore((s) => s.languageFilter);
  const themeFilter = useSubmissionsFilterStore((s) => s.themeFilter);
  const debouncedSearch = useDebounce(searchValue, 500);

  const [prevSearch, setPrevSearch] = useState(debouncedSearch);
  if (debouncedSearch !== prevSearch) {
    setPrevSearch(debouncedSearch);
    setPage(1);
  }

  const [prevLanguageFilter, setPrevLanguageFilter] = useState(languageFilter);
  if (languageFilter !== prevLanguageFilter) {
    setPrevLanguageFilter(languageFilter);
    setPage(1);
  }

  const [prevOrdering, setPrevOrdering] = useState(ordering);
  if (ordering !== prevOrdering) {
    setPrevOrdering(ordering);
    setPage(1);
  }

  const [prevThemeFilter, setPrevThemeFilter] = useState(themeFilter);
  if (themeFilter !== prevThemeFilter) {
    setPrevThemeFilter(themeFilter);
    setPage(1);
  }

  const query = useQuery({
    queryKey: [
      "submissions",
      {
        page,
        search: debouncedSearch,
        source_language: languageFilter,
        ordering,
        topic: themeFilter,
      },
    ],
    queryFn: () =>
      dilemmasRepository.fetchList({
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        status: DILEMMAS_STATUS.SUBMITTED,
        search: debouncedSearch || undefined,
        source_language: languageFilter ?? undefined,
        ordering: ordering ?? undefined,
        topic: themeFilter ?? undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const dilemmas = query.data?.results ?? [];
  const pageCount = query.data ? Math.ceil(query.data.count / PAGE_SIZE) : 0;

  return {
    dilemmas,
    pageCount,
    page,
    setPage,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
  };
}
